import { useEffect } from "react";
import { requestActivityPing } from "../webclients/pin.client";

/** Least delay between two pings. */
export const ACTIVITY_PING_INTERVAL_MS = 60_000;

const ACTIVITY_EVENTS = [
  "pointerdown",
  "pointermove",
  "keydown",
  "wheel",
  "scroll",
  "touchstart",
] as const;

/**
 * MED-1622: the server locks a session after the user's lock delay (MED-1644)
 * without an API call, but a user reading a page or filling a long form makes none. While `enabled`
 * (unlocked, with a PIN) and the tab is visible, user activity since the last
 * ping triggers POST /account/activity at most every 60 s. Fire-and-forget: a
 * 423 answer locks the session through the axios interceptor.
 */
export const useActivityHeartbeat = (enabled: boolean) => {
  useEffect(() => {
    if (!enabled) return;
    let lastPing = Date.now();
    let active = false;

    const pingIfDue = () => {
      if (!active || document.visibilityState === "hidden") return;
      if (Date.now() - lastPing < ACTIVITY_PING_INTERVAL_MS) return;
      lastPing = Date.now();
      active = false;
      requestActivityPing().catch(() => {
        // Handled by the interceptor (423 locks); nothing else to do.
      });
    };

    const onActivity = () => {
      active = true;
      pingIfDue();
    };

    const timer = setInterval(pingIfDue, ACTIVITY_PING_INTERVAL_MS);
    ACTIVITY_EVENTS.forEach(name =>
      document.addEventListener(name, onActivity, {
        capture: true,
        passive: true,
      })
    );
    return () => {
      clearInterval(timer);
      ACTIVITY_EVENTS.forEach(name =>
        document.removeEventListener(name, onActivity, { capture: true })
      );
    };
  }, [enabled]);
};
